import { useMemo } from "react";

import { createTheme } from "@mui/material/styles";
import {
  getDullPalette,
  getHSLFromHex,
  isDullMode,
  getLightPalette,
  getDarkPalette,
  getDullDarkPalette,
} from "@simplyk/common";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { assignGiftAidBlockComponentTokens } from "../../features/FormV2/blocks/selection/donation-selection/GiftAidBlock/GiftAidBlockTokens";

import { createCanvasTheme } from "./canvasTheme";

import { assignAvatarComponentTokens } from "@/forms-design-system/FormAvatar/FormAvatarTokens";
import { assignBlockComponentTokens } from "@/forms-design-system/FormBlock/FormBlockTokens";
import { assignButtonComponentTokens } from "@/forms-design-system/FormButton/FormButtonTokens";
import { assignOverlayButtonComponentTokens } from "@/forms-design-system/FormButton/FormOverlayButtonTokens";
import { assignCartWidgetComponentTokens } from "@/forms-design-system/FormCartWidget/FormCartWidgetTokens";
import { assignCheckboxComponentTokens } from "@/forms-design-system/FormCheckbox/FormCheckboxTokens";
import { assignChipComponentTokens } from "@/forms-design-system/FormChip/FormChipTokens";
import { assignCounterComponentTokens } from "@/forms-design-system/FormCounter/FormCounterTokens";
import { assignDateComponentTokens } from "@/forms-design-system/FormDate/FormDateTokens";
import { assignDatePickerFieldComponentTokens } from "@/forms-design-system/FormDatePickerField/FormDatePickerFieldTokens";
import { assignDateSelectFieldComponentTokens } from "@/forms-design-system/FormDateSelectField/FormDateSelectFieldTokens";
import { assignDialogComponentTokens } from "@/forms-design-system/FormDialog/FormDialogTokens";
import { assignDividerComponentTokens } from "@/forms-design-system/FormDivider/FormDividerTokens";
import { assignDrawerComponentTokens } from "@/forms-design-system/FormDrawer/FormDrawerTokens";
import { assignFieldComponentTokens } from "@/forms-design-system/FormField/FormFieldTokens";
import { assignInfoboxComponentTokens } from "@/forms-design-system/FormInfobox/FormInfoboxTokens";
import { assignLinkBlockComponentTokens } from "@/forms-design-system/FormLinkBlock/FormLinkBlockTokens";
import { assignLocationComponentTokens } from "@/forms-design-system/FormLocation/FormLocationTokens";
import { assignPaymentMethodComponentTokens } from "@/forms-design-system/FormPaymentMethod/FormPaymentMethodTokens";
import { assignPaymentSummaryComponentTokens } from "@/forms-design-system/FormPaymentSummary/FormPaymentSummaryTokens";
import { assignProgressBarComponentTokens } from "@/forms-design-system/FormProgressBar/FormProgressBarTokens";
import { assignRadioComponentTokens } from "@/forms-design-system/FormRadio/FormRadioTokens";
import { assignRadioSelectComponentTokens } from "@/forms-design-system/FormRadioSelect/FormRadioSelectTokens";
import { assignSelectFieldComponentTokens } from "@/forms-design-system/FormSelectField/FormSelectFieldTokens";
import { assignSkeletonComponentTokens } from "@/forms-design-system/FormSkeleton/FormSkeletonTokens";
import { assignTabsComponentTokens } from "@/forms-design-system/FormTab/FormTabsTokens";
import { assignTextButtonComponentTokens } from "@/forms-design-system/FormTextButton/FormTextButtonTokens";
import { assignTextFieldComponentTokens } from "@/forms-design-system/FormTextField/FormTextFieldTokens";
import { assignThermometerComponentTokens } from "@/forms-design-system/FormThermometer/FormThermometerTokens";
import { assignTooltipComponentTokens } from "@/forms-design-system/FormTooltip/FormTooltipTokens";
import { assignZeffyLoaderComponentTokens } from "@/forms-design-system/FormZeffyLoader/FormZeffyLoaderTokens";
import { assignGlobalTokens } from "@/forms-design-system/GlobalTokens";

export const useDynamicCanvasTheme = (baseHexColor: string, mode: "light" | "dark") => {
  return useMemo(() => createDynamicCanvasTheme(baseHexColor, mode), [baseHexColor, mode]);
};

export const createDynamicCanvasTheme = (baseHexColor: string, mode: "light" | "dark") => {
  const { h: baseHue } = getHSLFromHex(baseHexColor);
  const isDull = isDullMode(baseHexColor);

  const palette = isDull
    ? mode === "light"
      ? getDullPalette()
      : getDullDarkPalette()
    : mode === "light"
      ? getLightPalette(baseHue)
      : getDarkPalette(baseHue);
  const finalizedPalette = {
    ...palette,
    formsComponents: {
      global: assignGlobalTokens(palette.forms),
      button: assignButtonComponentTokens(palette.forms, mode),
      cartWidget: assignCartWidgetComponentTokens(palette.forms, mode, isDull),
      divider: assignDividerComponentTokens(palette.forms, mode),
      counter: assignCounterComponentTokens(palette.forms, mode),
      infobox: assignInfoboxComponentTokens(palette.forms, mode),
      progressBar: assignProgressBarComponentTokens(palette.forms, mode),
      thermometer: assignThermometerComponentTokens(palette.forms, mode, isDull),
      tooltip: assignTooltipComponentTokens(palette.forms, mode),
      date: assignDateComponentTokens(palette.forms, mode),
      location: assignLocationComponentTokens(palette.forms, mode),
      field: assignFieldComponentTokens(palette.forms, mode),
      textField: assignTextFieldComponentTokens(palette.forms, mode),
      chip: assignChipComponentTokens(palette.forms, mode),
      radio: assignRadioComponentTokens(palette.forms, mode),
      radioSelect: assignRadioSelectComponentTokens(palette.forms, mode),
      selectField: assignSelectFieldComponentTokens(palette.forms, mode, isDull),
      dateSelectField: assignDateSelectFieldComponentTokens(palette.forms, mode),
      tabs: assignTabsComponentTokens(palette.forms, mode),
      dialog: assignDialogComponentTokens(palette.forms, mode),
      drawer: assignDrawerComponentTokens(palette.forms, mode),
      checkbox: assignCheckboxComponentTokens(palette.forms, mode),
      paymentSummary: assignPaymentSummaryComponentTokens(palette.forms, mode),
      textButton: assignTextButtonComponentTokens(palette.forms, mode),
      block: assignBlockComponentTokens(palette.forms, mode, isDull),
      linkBlock: assignLinkBlockComponentTokens(palette.forms, mode),
      avatar: assignAvatarComponentTokens(palette.forms, mode, isDull),
      skeleton: assignSkeletonComponentTokens(palette.forms, mode),
      paymentMethod: assignPaymentMethodComponentTokens(palette.forms, mode),
      overlayButton: assignOverlayButtonComponentTokens(palette.forms, mode, isDull),
      datePickerField: assignDatePickerFieldComponentTokens(palette.forms, mode, isDull),
      zeffyLoader: assignZeffyLoaderComponentTokens(palette.forms, mode),
      giftAidBlock: assignGiftAidBlockComponentTokens(palette.forms, mode),
    },
  };

  const baseTheme = createCanvasTheme(baseHexColor);

  const mergedPalette = {
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      ...finalizedPalette,
      metadata: {
        isDynamicTheme: true,
        baseColor: baseHexColor,
        mode,
      },
    },
    components: {
      MuiInputBase: {
        defaultProps: {
          disableInjectingGlobalStyles: true,
        },
      },
    },
  };

  return createTheme(mergedPalette);
};
